import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Check, ArrowRight, Pencil } from "lucide-react";

// Fallback copy function for browsers that don't support clipboard API
const copyToClipboard = async (text) => {
  // Try modern clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Fall through to fallback
    }
  }
  
  // Fallback: Create a temporary textarea
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    document.execCommand("copy");
    textArea.remove();
    return true;
  } catch (err) {
    textArea.remove();
    return false;
  }
};

export default function SuggestionCard({ suggestion, index, onUse }) {
  const [copied, setCopied] = useState(false);

  const showPersonalizationTip = () => {
    setTimeout(() => {
      toast(
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#EAF6F4] flex items-center justify-center flex-shrink-0">
            <Pencil size={14} className="text-[#4FB3A5]" />
          </div>
          <div>
            <p className="font-medium text-[#1F3A5F] text-sm">Tip</p>
            <p className="text-[#6B7280] text-xs mt-0.5">Tweak this so it sounds like you. A personal touch makes it more authentic.</p>
          </div>
        </div>,
        {
          duration: 4000,
          className: "bg-white border border-[#E5E7EB]",
        }
      );
    }, 800);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    const success = await copyToClipboard(suggestion.text);
    
    if (success) {
      setCopied(true);
      toast.success("Copied to clipboard ✓");
      showPersonalizationTip();
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleUse = () => {
    onUse(suggestion.text);
    toast.success("Added — feel free to edit it");
    showPersonalizationTip();
  };

  const toneStyles = {
    calm: { bg: "bg-[#EAF6F4]", text: "text-[#4FB3A5]" },
    empathetic: { bg: "bg-[#ECFDF5]", text: "text-[#059669]" },
    curious: { bg: "bg-[#E0F2FE]", text: "text-[#0284C7]" },
    vulnerable: { bg: "bg-[#FDF2F8]", text: "text-[#DB2777]" },
    direct: { bg: "bg-[#F1F5F9]", text: "text-[#475569]" },
    warm: { bg: "bg-[#FFF7ED]", text: "text-[#EA580C]" },
    understanding: { bg: "bg-[#EDE9FE]", text: "text-[#7C3AED]" },
    assertive: { bg: "bg-[#FEF9E7]", text: "text-[#B8860B]" },
    supportive: { bg: "bg-[#ECFDF5]", text: "text-[#059669]" },
    honest: { bg: "bg-[#F1F5F9]", text: "text-[#475569]" },
    gentle: { bg: "bg-[#FDF2F8]", text: "text-[#DB2777]" }
  };

  const getToneStyle = (tone) => {
    const lowerTone = tone.toLowerCase();
    for (const [key, value] of Object.entries(toneStyles)) {
      if (lowerTone.includes(key)) return value;
    }
    return { bg: "bg-[#F1F5F9]", text: "text-[#6B7280]" };
  };

  const style = getToneStyle(suggestion.tone);

  return (
    <div 
      className="bg-[#EAF6F4] border border-[#4FB3A5]/20 rounded-xl p-5 
                 transition-all duration-300 animate-fadeIn"
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`suggestion-${index}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge 
            className={`${style.bg} ${style.text} border-0 text-xs font-semibold py-1.5 px-3 rounded-lg`}
          >
            {suggestion.tone}
          </Badge>
          <span className="text-xs text-[#6B7280] font-medium">
            {suggestion.approach}
          </span>
        </div>
      </div>
      
      <p 
        className="text-[#2C2C2C] leading-relaxed mb-5 bg-white p-4 rounded-xl border border-[#E5E7EB]"
        data-testid={`suggestion-text-${index}`}
      >
        "{suggestion.text}"
      </p>
      
      <div className="flex gap-3">
        <Button
          onClick={handleUse}
          className="flex-1 gap-2 bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-medium rounded-xl py-5"
          data-testid={`use-suggestion-${index}`}
        >
          <ArrowRight size={16} />
          Use this
        </Button>
        <Button
          variant="outline"
          onClick={handleCopy}
          className="gap-2 border-2 border-[#4FB3A5] text-[#1F3A5F] hover:bg-white rounded-xl px-4"
          data-testid={`copy-suggestion-${index}`}
        >
          {copied ? (
            <>
              <Check size={16} className="text-[#7BC4A4]" />
              Copied
            </>
          ) : (
            <>
              <Copy size={16} className="text-[#4FB3A5]" />
              Copy
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
